import type {
  AddLocationFormData,
  BaseLocationDto,
  CityDto,
  CommentDto,
  DetailLocationDto,
  GeoCoords,
  GooglePlaceQuery,
  GooglePlaceResultDto,
  ImageDto,
  MapClusterData,
  MapLocationDto,
  SearchLocationDto,
  SearchLocationOptions,
} from '@/core/data/location/location.interface';
import { LocationRepository } from '@/core/data/location/location.repository';
import { LocationChessFeature, LocationType, MapLocationType } from '@/core/data/location/location.type';
import type { SparkSegment } from '@/core/data/spark/spark.interface';
import FormHelper from '@/core/helpers/form.helper';
import LocationHelper from '@/core/helpers/location.helper';
import type { ApiError, Result } from '@/core/network/http/httpClient.interface';
import { generalError } from '@/core/network/http/httpError';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import type { AddCommentForm, LocationFormProps } from '@/views/composables/models/form.interface';
import { Cluster, MarkerClusterer } from '@googlemaps/markerclusterer';
import AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement;

export class LocationService {
  private readonly repository: LocationRepository;
  private clusterer?: MarkerClusterer;
  private mapMarkers: AdvancedMarkerElement[];
  private hiddenInactiveMarkers: AdvancedMarkerElement[] = [];
  private hiddenInactiveItems: MapLocationDto[] = [];
  private dotMarkers: AdvancedMarkerElement[] = [];
  private _onClickMarker?: (item: MapLocationDto) => void;
  private _onClickChessboard?: (item: MapLocationDto) => void;

  constructor(repository: LocationRepository) {
    this.repository = repository;
    this.mapMarkers = [];
  }

  // async getCity(city: string, country: string, coordinates: GeoCoords): Promise<Result<CityDto>> {
  //   try {
  //     const response = await this.repository.getCity(city, country, coordinates);
  //     return response?.data ? { data: response.data, cursor: response.cursor } : { error: generalError() };
  //   } catch (error) {
  //     return {
  //       error: error as ApiError,
  //     };
  //   }
  // }

  async getCityFor(coordinates: GeoCoords): Promise<Result<CityDto>> {
    try {
      const response = await this.repository.getCityFor(coordinates);
      return response?.data ? { data: response.data, cursor: response.cursor } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getLocations(listId: GamListId, geo: GeoCoords, radius: number): Promise<Result<BaseLocationDto[]>> {
    try {
      const response = await this.repository.getLocations(listId, geo, radius);
      return response?.data ? { data: response.data, cursor: response.cursor } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getLocationsByCoordinates(
    geo: GeoCoords,
    options?: {
      distance?: number;
      perPage?: number;
      intentDate?: string;
      availabilityDate?: string;
    },
  ): Promise<Result<BaseLocationDto[]>> {
    try {
      const response = await this.repository.getLocationsByCoordinates(geo, options);
      return response?.data ? { data: response.data, cursor: response.cursor } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getSparkableLocationsByCoordinates(
    geo: GeoCoords,
    options?: {
      distance?: number;
      perPage?: number;
      intentDate?: string;
      availabilityDate?: string;
    },
  ): Promise<Result<BaseLocationDto[]>> {
    try {
      const response = await this.repository.getSparkableLocationsByCoordinates(geo, options);
      return response?.data ? { data: response.data, cursor: response.cursor } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getAvailabilities(
    locationIds: string[],
    day: number,
    startTime: number,
    endTime: number,
  ): Promise<Result<SparkSegment[]>> {
    try {
      const response = await this.repository.getAvailabilities(locationIds, day, startTime, endTime);
      return response?.data ? { data: response.data, cursor: response.cursor } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getMapLocations(listId: GamListId, center: GeoCoords, radius: number): Promise<Result<MapLocationDto[]>> {
    try {
      const response = await this.repository.getMapLocations(listId, center, radius);
      return response?.data ? { data: response.data, cursor: response.cursor } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async searchLocationsByQuery(query?: string, options?: SearchLocationOptions): Promise<Result<SearchLocationDto[]>> {
    try {
      const response = await this.repository.searchLocationsByQuery(query, options);
      return response?.data ? { data: response.data, cursor: response.cursor } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async fetchGooglePlaceDetails(payload: GooglePlaceQuery): Promise<Result<GooglePlaceResultDto>> {
    try {
      const response = await this.repository.fetchGooglePlaceDetails(payload);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async reverseGeocode(lat: number, lng: number): Promise<Result<SearchLocationDto>> {
    try {
      const response = await this.repository.reverseGeocode(lat, lng);
      return response?.data ? { data: response.data, cursor: response.cursor } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async createFirestoreDoc(id: string): Promise<Result<any>> {
    try {
      const response = await this.repository.createFirestoreDoc(id);
      return response?.data ? response : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getLocation(id: string): Promise<Result<DetailLocationDto>> {
    try {
      const response = await this.repository.getLocation(id);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getLocationCover(id: string): Promise<Result<string | null>> {
    try {
      const response = await this.repository.getLocationCover(id);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async getLocationImages(id: string): Promise<Result<ImageDto[]>> {
    try {
      const response = await this.repository.getLocationImages(id);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  // async getLocationComments(id: string): Promise<Result<CommentDto[]>> {
  //   try {
  //     const response = await this.repository.getLocationComments(id);
  //     return response?.data ? { data: response.data } : { error: generalError() };
  //   } catch (error) {
  //     return {
  //       error: error as ApiError,
  //     };
  //   }
  // }

  private mapLocationToPin(id: string, data: AddLocationFormData): MapLocationDto {
    return {
      id,
      mapLocationType: MapLocationType.LOCATION,
      name: data.name,
      coordinates: {
        lat: data.coordinates?.lat || 0,
        lng: data.coordinates?.lng || 0,
      },
      type: data.type,
      chessFeatures: data.chessFeatures.map((type) => {
        return type.toString() as LocationChessFeature;
      }),
      private: data.private,
      favorite: false,
      nextGambits: null,
    };
  }

  async addNewLocation(formData: LocationFormProps): Promise<Result<MapLocationDto>> {
    try {
      const data: AddLocationFormData = {
        name: formData.name,
        address: formData.address,
        about: formData.about || undefined,
        coordinates: formData.coordinates,
        chessSetCount: formData.chessSetCount,
        chessClockCount: formData.chessClockCount,
        chessTableCount: formData.chessTableCount,
        giantChessBoardCount: formData.giantChessBoardCount,
        giantChessPieces: formData.giantChessPieces,
        type: formData.type as LocationType,
        chessFeatures: formData.chessFeatures,
        generalFeatures: formData.generalFeatures,
        images: (formData.photos || []).map((photo) => FormHelper.parseImageFile(photo)),
        private: formData.private,
        openingHours: formData.openingHours,
      };

      const response = await this.repository.addNewLocation(data);

      return response?.data ? { data: this.mapLocationToPin(response.data.id, data) } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  getSelectedPinElement(id: string): HTMLDivElement {
    return this.repository.getSelectedPin(id)?.element;
  }

  private shouldOutcluster(item: MapLocationDto): boolean {
    return (
      (Array.isArray(item.nextGambits) && item.nextGambits.length > 0) ||
      Number(item.visitorCount) > 0 ||
      Number(item.activeChessboardCount) > 0
    );
  }

  private shouldAlwaysBeVisible(item: MapLocationDto): boolean {
    return this.shouldOutcluster(item);
  }

  public hasActiveSpots(items: MapLocationDto[]): boolean {
    return items.some((item) => this.shouldOutcluster(item));
  }

  public async addItems(
    items: MapLocationDto[],
    onClickMarker: (item: MapLocationDto) => void,
    showAll = false,
    activeSpotId?: string,
    onClickChessboard?: (item: MapLocationDto) => void,
  ) {
    this._onClickMarker = onClickMarker;
    this._onClickChessboard = onClickChessboard;

    const standaloneItems = items.filter((item) => this.shouldOutcluster(item));
    const inactiveItems = items.filter((item) => !this.shouldOutcluster(item));

    if (showAll) {
      const clusterMarkers = await this.repository.getBaseMarkers({
        items: inactiveItems,
        onClickMarker,
        onClickChessboard,
        activeSpotId,
      });
      this.mapMarkers = [...this.mapMarkers, ...clusterMarkers];
      if (this.clusterer) {
        this.clusterer.addMarkers(clusterMarkers);
      }
    } else {
      this.hiddenInactiveItems = [...this.hiddenInactiveItems, ...inactiveItems];

      const map = (this.clusterer?.getMap() as google.maps.Map) ?? null;
      if (inactiveItems.length) {
        const dots = await this.repository.getBaseMarkers({
          items: inactiveItems,
          onClickMarker,
          onClickChessboard,
          activeSpotId,
          dotMode: true,
        });
        for (const dot of dots) {
          dot.map = map;
        }
        this.dotMarkers = [...this.dotMarkers, ...dots];
        this.mapMarkers = [...this.mapMarkers, ...dots];
      }
    }

    const standaloneMarkers = await this.repository.getBaseMarkers({
      items: standaloneItems,
      onClickMarker,
      onClickChessboard,
      activeSpotId,
    });
    const map = (this.clusterer?.getMap() as google.maps.Map) ?? null;
    for (const marker of standaloneMarkers) {
      marker.map = map;
    }
    this.mapMarkers = [...this.mapMarkers, ...standaloneMarkers];
  }

  public async removeItems(items: MapLocationDto[]) {
    const staleMarkers: AdvancedMarkerElement[] = [];
    const outgoingIds = new Set(items.map((item) => item.id));

    this.mapMarkers = this.mapMarkers.filter((marker) => {
      if (outgoingIds.has(marker.id)) {
        staleMarkers.push(marker);
        return false;
      }

      return true;
    });

    this.hiddenInactiveMarkers = this.hiddenInactiveMarkers.filter((m) => !outgoingIds.has(m.id));
    this.hiddenInactiveItems = this.hiddenInactiveItems.filter((i) => !outgoingIds.has(i.id));

    const staleDots = this.dotMarkers.filter((m) => outgoingIds.has(m.id));
    this.dotMarkers = this.dotMarkers.filter((m) => !outgoingIds.has(m.id));
    for (const dot of staleDots) {
      dot.map = null;
    }

    this.clusterer?.removeMarkers(staleMarkers);
    for (const marker of staleMarkers) {
      marker.map = null;
    }
  }

  public async showInactiveMarkers() {
    if (!this.hiddenInactiveItems.length || !this._onClickMarker) return;

    for (const dot of this.dotMarkers) {
      dot.map = null;
    }
    const dotIds = new Set(this.dotMarkers.map((m) => m.id));
    this.mapMarkers = this.mapMarkers.filter((m) => !dotIds.has(m.id));
    this.dotMarkers = [];

    const markers = await this.repository.getBaseMarkers({
      items: this.hiddenInactiveItems,
      onClickMarker: this._onClickMarker,
      onClickChessboard: this._onClickChessboard,
    });
    this.hiddenInactiveMarkers = markers;
    this.mapMarkers = [...this.mapMarkers, ...markers];
    if (this.clusterer) {
      this.clusterer.addMarkers(markers);
    }
    this.hiddenInactiveItems = [];
  }

  public async hideInactiveMarkers(protectedId?: string) {
    const toHide: AdvancedMarkerElement[] = [];
    const remaining: AdvancedMarkerElement[] = [];
    const inactiveItems: MapLocationDto[] = [];

    for (const marker of this.mapMarkers) {
      if (protectedId && marker.id === protectedId) {
        remaining.push(marker);
        continue;
      }
      const pinData = this.repository.getPinLocationData(marker.id);
      if (pinData && !this.shouldAlwaysBeVisible(pinData)) {
        toHide.push(marker);
        inactiveItems.push(pinData);
      } else {
        remaining.push(marker);
      }
    }

    if (!toHide.length) return;

    this.clusterer?.removeMarkers(toHide);
    for (const marker of toHide) {
      marker.map = null;
    }
    this.hiddenInactiveMarkers = [];
    this.hiddenInactiveItems = inactiveItems;

    const map = (this.clusterer?.getMap() as google.maps.Map) ?? null;
    if (inactiveItems.length && this._onClickMarker) {
      const dots = await this.repository.getBaseMarkers({
        items: inactiveItems,
        onClickMarker: this._onClickMarker,
        onClickChessboard: this._onClickChessboard,
        dotMode: true,
      });
      for (const dot of dots) {
        dot.map = map;
      }
      this.dotMarkers = dots;
      remaining.push(...dots);
    }

    this.mapMarkers = remaining;
  }

  async getMarkerCluster(data: MapClusterData): Promise<void> {
    // this.clusterer?.clearMarkers();
    // this.clusterer?.removeMarkers()

    // const currentItems = await this.setMapMarkers(data);
    if (this.clusterer) {
      return;
    }

    const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;

    this.clusterer = new MarkerClusterer({
      map: data.map,
      algorithm: this.repository.getClusterAlgorithm(),
      markers: [],
      onClusterClick: (event: google.maps.MapMouseEvent, cluster: Cluster, map: google.maps.Map) => {
        const bounds = cluster.bounds;

        if (cluster.bounds) {
          const center = bounds?.getCenter();
          const zoom = map.getZoom();

          if (!center || !zoom || !data.onClickMarker) return;

          event.stop();

          const item: MapLocationDto = {
            id: '',
            nextGambits: null,
            mapLocationType: MapLocationType.CLUSTER,
            coordinates: { lat: center.lat(), lng: center.lng() },
          };

          data.onClickMarker(item);
        }
      },
      renderer: {
        render: ({ count, position }) => {
          return new AdvancedMarkerElement({
            position,
            content: this.repository.getClusterPin(count),
          });
        },
      },
    });
  }

  getPinLocationData(id: string): MapLocationDto | undefined {
    return this.repository.getPinLocationData(id);
  }

  refreshBoardOnlyMarkers(): void {
    for (const marker of this.mapMarkers) {
      const data = this.repository.getPinLocationData(marker.id);
      if (!data) continue;
      const hasBoards = Number(data.activeChessboardCount) > 0;
      const hasIrl = Number(data.visitorCount) > 0 || (Array.isArray(data.nextGambits) && data.nextGambits.length > 0);
      if (hasBoards && !hasIrl) {
        marker.content = this.repository.updateSelectedPin(data).element;
        marker.zIndex = this.repository.getMarkerZIndex(data);
      }
    }
  }

  updateMarker(item: MapLocationDto, selected?: string): void {
    const selectedMarker = this.mapMarkers.find((marker) => {
      return marker.id === item.id;
    });

    if (selectedMarker) {
      const existing = this.repository.getPinLocationData(item.id);
      if (existing) {
        item.hasPlayableBoard ??= existing.hasPlayableBoard;
      }
      selectedMarker.content = this.repository.updateSelectedPin(item, selected).element;
      selectedMarker.zIndex = this.repository.getMarkerZIndex(item, selected);
    }
  }

  updateMarkerCheckInStatus(locationId: string, checkedIn: boolean): void {
    const locationData = this.repository.getPinLocationData(locationId);
    if (!locationData) return;

    const current = locationData.visitorCount ?? 0;
    locationData.visitorCount = checkedIn ? current + 1 : Math.max(0, current - 1);

    const marker = this.mapMarkers.find((m) => m.id === locationId);
    if (marker) {
      marker.content = this.repository.updateSelectedPin(locationData).element;
      marker.zIndex = this.repository.getMarkerZIndex(locationData);
    }
  }

  updateMarkerFavoriteStatus(locationId: string, favorite: boolean): void {
    const locationData = this.repository.getPinLocationData(locationId);
    if (!locationData) return;

    locationData.favorite = favorite;

    const marker = this.mapMarkers.find((m) => m.id === locationId);
    if (marker) {
      marker.content = this.repository.updateSelectedPin(locationData).element;
      marker.zIndex = this.repository.getMarkerZIndex(locationData);
    }
  }

  async toggleFavorite(id: string, favorite?: boolean): Promise<Result<boolean>> {
    try {
      const response = await this.repository.toggleFavorite(id, favorite);
      return response ? { data: true } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async toggleImageLike(id: string, like: boolean): Promise<Result<boolean>> {
    try {
      const response = await this.repository.toggleImageLike(id, like);
      return response ? { data: true } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  private async setAddedImage(image: ImageDto): Promise<ImageDto> {
    return {
      id: image.id,
      imageBlobHash: image.imageBlobHash,
      numberOfLikes: 0,
      liked: false,
      user: await LocationHelper.authAuthor(),
      createdAt: image.createdAt,
    };
  }

  async addLocationPhoto(id: string, photo: string): Promise<Result<ImageDto>> {
    try {
      const response = await this.repository.addPhoto(id, FormHelper.parseImageFile(photo));
      return response?.data
        ? {
            data: await this.setAddedImage(response.data),
          }
        : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async pinCover(locationId: string, imageBlobHash: string | null): Promise<Result<{ coverBlobHash: string | null }>> {
    try {
      const response = await this.repository.pinCover(locationId, imageBlobHash);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async deleteLocationImage(id: string): Promise<Result<boolean>> {
    try {
      const response = await this.repository.deleteLocationImage(id);
      return response ? { data: true } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async addNewComment(id: string, formData: AddCommentForm): Promise<Result<CommentDto>> {
    try {
      const response = await this.repository.addNewComment(id, {
        rating: formData.rating,
        message: formData.message,
        image: FormHelper.parseImageFile(formData.photo),
      });
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async deleteComment(id: string): Promise<Result<boolean>> {
    try {
      const response = await this.repository.deleteComment(id);
      return response ? { data: response } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async destroyMap(): Promise<void> {
    this.repository.clearMapPins();
    this.clusterer?.clearMarkers();
    this.clusterer?.onRemove();

    return new Promise((resolve) => {
      for (const marker of this.mapMarkers) {
        marker.map = null;
      }
      this.mapMarkers = [];
      this.hiddenInactiveMarkers = [];
      this.hiddenInactiveItems = [];
      this.dotMarkers = [];
      this._onClickMarker = undefined;
      this._onClickChessboard = undefined;
      this.clusterer = undefined;
      resolve();
    });
  }

  async checkIn(id: string, options?: { gambitId?: string; ignoreGambit?: boolean }): Promise<Result<any> | null> {
    try {
      const response = await this.repository.checkIn(id, options);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async updateCheckIn(id: string, data: { chessSetCount?: number }): Promise<Result<any> | null> {
    try {
      const response = await this.repository.updateCheckIn(id, data);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async checkOut(id: string): Promise<Result<any> | null> {
    try {
      const response = await this.repository.checkOut(id);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }
}
